// src/pages/Home.tsx
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Award, Clock, Users } from 'lucide-react';
import Hero from '../components/sections/Hero';
import ServiceCarousel from '../components/ui/ServiceCarousel';
import TestimonialCarousel from '../components/ui/TestimonialCarousel';
import GalleryCarousel from '../components/ui/GalleryCarousel';
import BookingModal from '../components/ui/BookingModal';
import { services } from '../data/services';
import { testimonials } from '../data/testimonials';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { fadeInUp, staggerContainer } from '../utils/animations';

const Home: React.FC = () => {
  const { ref: statsRef, controls: statsControls } = useScrollAnimation();
  const { ref: ctaRef, controls: ctaControls } = useScrollAnimation();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<string>('');

  // Stats data
  const stats = [
    { icon: <Users size={24} />, value: '500+', label: 'Happy Clients in Accra' },
    { icon: <Award size={24} />, value: '4.9', label: 'Star Rating Average' },
    { icon: <Clock size={24} />, value: '5+', label: 'Years of Excellence' },
    { icon: <Sparkles size={24} />, value: '200+', label: 'Nail Art Designs' },
  ];

  // Handle booking from service cards
  const handleBookService = (serviceId: string) => {
    setSelectedService(serviceId);
    setIsBookingModalOpen(true);
  };

  // Get popular services for featured section
  const popularServices = services.filter(service => service.popular).slice(0, 4);

  // Get featured testimonials
  const featuredTestimonials = testimonials.slice(0, 3);

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="overflow-hidden"
    >
      {/* Hero Section */}
      <Hero />

      {/* Service Carousel Section */}
      <section className="section-padding bg-gradient-to-b from-white to-mauve-50">
        <div className="container-custom">
          <ServiceCarousel />
        </div>
      </section>

      {/* Quick Stats Section */}
      <section ref={statsRef} className="section-padding bg-white">
        <motion.div
          initial="initial"
          animate={statsControls}
          variants={staggerContainer}
          className="container-custom"
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-mauve-900 mb-4">
              Excellence in Numbers
            </h2>
            <p className="text-lg text-mauve-600 max-w-2xl mx-auto">
              Trusted by professionals and beauty enthusiasts across Greater Accra
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                custom={index}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-mauve-100 rounded-2xl mb-4">
                  <div className="text-mauve-500">{stat.icon}</div>
                </div>
                <div className="text-3xl font-bold text-mauve-900 mb-2">{stat.value}</div>
                <div className="text-mauve-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-gradient-to-b from-mauve-50 to-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-mauve-900 mb-6">
                Why Choose GetNailedbyAJ
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-mauve-100 p-3 rounded-lg">
                    <Sparkles className="text-mauve-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-mauve-900 mb-2">
                      Authentic Ghanaian Ingredients
                    </h3>
                    <p className="text-mauve-700">
                      We incorporate locally sourced gel and coconut oil 
                      in our treatments for natural, effective results.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-mauve-100 p-3 rounded-lg">
                    <Award className="text-mauve-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-mauve-900 mb-2">
                      International Standards
                    </h3>
                    <p className="text-mauve-700">
                      Certified technicians trained in Europe and Asia bring world-class 
                      techniques to Accra's nail care scene.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-mauve-100 p-3 rounded-lg">
                    <Clock className="text-mauve-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-mauve-900 mb-2">
                      Flexible Scheduling
                    </h3>
                    <p className="text-mauve-700">
                      Extended hours, weekend appointments, and mobile services 
                      throughout Greater Accra to fit your busy schedule.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-mauve-400 to-mauve-600 rounded-3xl p-8 text-white">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold mb-4">Book Your Appointment</h3>
                  <p className="opacity-90">
                    Experience premium nail care in our Hatso salon or request 
                    mobile services for your convenience.
                  </p>
                </div>

                <div className="space-y-4">
                  <button
                    onClick={() => setIsBookingModalOpen(true)}
                    className="w-full bg-white text-mauve-700 hover:bg-mauve-50 font-medium py-3 rounded-lg transition-colors"
                  >
                    Book Salon Appointment
                  </button>
                  
                  <button className="w-full border-2 border-white text-white hover:bg-white/10 font-medium py-3 rounded-lg transition-colors">
                    Request Mobile Service
                  </button>
                </div>

                <div className="mt-8 pt-8 border-t border-white/20">
                  <div className="text-sm">
                    <p className="font-medium mb-2">Special Offers:</p>
                    <ul className="space-y-1">
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                        10% off first-time clients
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                        Student discount available
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                        Corporate package rates
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-mauve-200 rounded-full opacity-50"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-mauve-300 rounded-full opacity-30"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonial Carousel Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <TestimonialCarousel />
        </div>
      </section>

      {/* Gallery Carousel Section */}
      <section className="section-padding bg-mauve-50">
        <div className="container-custom">
          <GalleryCarousel />
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="section-padding bg-gradient-to-r from-mauve-700 to-mauve-900">
        <motion.div
          initial="initial"
          animate={ctaControls}
          variants={staggerContainer}
          className="container-custom text-center"
        >
          <motion.h2 
            variants={fadeInUp}
            className="text-4xl font-bold text-white mb-6"
          >
            Ready for Your Nail Transformation?
          </motion.h2>
          
          <motion.p 
            variants={fadeInUp}
            className="text-xl text-white/90 max-w-2xl mx-auto mb-8"
          >
            Book your appointment today and experience premium nail care in the heart of Accra.
          </motion.p>
          
          <motion.div 
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="px-8 py-3 bg-white text-mauve-900 font-bold rounded-lg hover:bg-mauve-50 transition-colors"
            >
              Book Now
            </button>
            
            <a
              href="tel:+233594197765"
              className="px-8 py-3 border-2 border-white text-white font-bold rounded-lg hover:bg-white/10 transition-colors"
            >
              Call: 059 419 7765
            </a>
          </motion.div>
          
          <motion.div 
            variants={fadeInUp}
            className="mt-8 text-white/80 text-sm"
          >
            <p> Hatso, Accra •  Open 7 Days a Week •  Professional Nail Care</p>
          </motion.div>
        </motion.div>
      </section>

      {/* Booking Modal */}
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        selectedService={selectedService}
      />
    </motion.main>
  );
};

export default Home;